/**
 * DhssUniversePicker — sortable/filterable table over GET /strategy/dhss-universe,
 * replacing manual symbol entry for DHSS in Portfolio Builder with a pick-from-list
 * flow. See docs/active_plans/DHSS_INSTRUMENT_SCREENER_PLAN.md §7.
 *
 * Props
 * ─────
 * selected      Set<string> of currently-checked symbols (controlled by parent)
 * onChange      (nextSelectedSet) => void
 * disabled      bool — true while a plan job is running
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Table, Input, Button, Spinner, Row, Col, Badge,
} from "reactstrap";

const SORT_OPTIONS = [
  { value: "suitability_score", label: "Suitability" },
  { value: "liquidity_score", label: "Liquidity" },
  { value: "est_roi_annualized_pct", label: "Est. ROI (annualized)" },
  { value: "market_cap", label: "Market Cap" },
];

const TIER_COLOR = { A: "#28a745", B: "#f0ad4e", C: "#dc3545" };

const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const fmtScore = (v) => (v == null ? "—" : Number(v).toFixed(2));
const fmtMoney = (v) =>
  v == null ? "—" : `$${(Number(v) / 1e9).toFixed(1)}B`;

const DhssUniversePicker = ({ selected, onChange, disabled }) => {
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sort, setSort] = useState("suitability_score");
  const [tier, setTier] = useState("");
  const [sicMajorGroup, setSicMajorGroup] = useState("");
  const [minLiquidity, setMinLiquidity] = useState(0);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get("/strategy/dhss-universe", {
        params: {
          sort,
          direction: "desc",
          tier: tier || undefined,
          sic_major_group: sicMajorGroup || undefined,
          min_liquidity: minLiquidity > 0 ? minLiquidity : undefined,
          search: search || undefined,
          limit: 200,
        },
      })
      .then((res) => setRows(res.data.results || []))
      .catch(() => setError("Failed to load the DHSS instrument universe."))
      .finally(() => setLoading(false));
  }, [sort, tier, sicMajorGroup, minLiquidity, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    axios
      .get("/strategy/dhss-universe/sectors")
      .then((res) => setSectors(res.data.sectors || []))
      .catch(() => setSectors([]));
  }, []);

  const toggle = (symbol) => {
    const next = new Set(selected);
    if (next.has(symbol)) next.delete(symbol);
    else next.add(symbol);
    onChange(next);
  };

  const selectAllVisible = () => {
    const next = new Set(selected);
    rows.forEach((r) => next.add(r.symbol));
    onChange(next);
  };

  const clearAll = () => onChange(new Set());

  return (
    <div>
      {error && <div className="text-danger mb-2" style={{ fontSize: 13 }}>{error}</div>}

      <Row className="mb-2">
        <Col md={3}>
          <Input
            type="select" bsSize="sm" value={sort}
            onChange={(e) => setSort(e.target.value)}
            disabled={disabled}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Sort: {o.label}</option>
            ))}
          </Input>
        </Col>
        <Col md={2}>
          <Input
            type="select" bsSize="sm" value={tier}
            onChange={(e) => setTier(e.target.value)}
            disabled={disabled}
          >
            <option value="">All tiers</option>
            <option value="A">Tier A</option>
            <option value="B">Tier B</option>
            <option value="C">Tier C</option>
          </Input>
        </Col>
        <Col md={3}>
          <Input
            type="select" bsSize="sm" value={sicMajorGroup}
            onChange={(e) => setSicMajorGroup(e.target.value)}
            disabled={disabled}
          >
            <option value="">All sectors</option>
            {sectors.map((s) => (
              <option key={s.sic_major_group} value={s.sic_major_group}>
                {s.sic_major_group} — {s.example_description} ({s.n})
              </option>
            ))}
          </Input>
        </Col>
        <Col md={4}>
          <Input
            type="text" bsSize="sm" placeholder="Search symbol or name..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {rows.length} instrument(s) shown · {selected.size} selected
        </small>
        <div>
          <Button size="sm" color="link" onClick={selectAllVisible} disabled={disabled || loading}>
            Select all shown
          </Button>
          <Button size="sm" color="link" onClick={clearAll} disabled={disabled || selected.size === 0}>
            Clear selection
          </Button>
          <Button size="sm" color="link" onClick={load} disabled={disabled || loading}>
            {loading ? <Spinner size="sm" /> : "Refresh"}
          </Button>
        </div>
      </div>

      <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
        <Table size="sm" hover style={{ marginBottom: 0, fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th></th>
              <th>Symbol</th>
              <th>Sector</th>
              <th>Mkt Cap</th>
              <th>Liquidity</th>
              <th>Est. ROI (ann.)</th>
              <th>Suitability</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-3"><Spinner size="sm" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-3">
                No scored instruments match these filters yet — try widening them,
                or run scripts/scan_dhss_universe.py to populate the screener.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.symbol} style={{ cursor: "pointer" }} onClick={() => !disabled && toggle(r.symbol)}>
                <td>
                  <Input
                    type="checkbox" checked={selected.has(r.symbol)}
                    onChange={() => toggle(r.symbol)} disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td style={{ fontWeight: 600 }}>{r.symbol}</td>
                <td style={{ color: "#888" }} title={r.sic_description || ""}>
                  {r.sic_major_group || "—"}
                </td>
                <td>{fmtMoney(r.market_cap)}</td>
                <td>
                  {fmtScore(r.liquidity_score)}{" "}
                  <Badge style={{ backgroundColor: TIER_COLOR[r.liquidity_tier] || "#aaa" }}>
                    {r.liquidity_tier || "—"}
                  </Badge>
                </td>
                <td>{fmtPct(r.est_roi_annualized_pct)}</td>
                <td>
                  {fmtScore(r.suitability_score)}{" "}
                  <Badge style={{ backgroundColor: TIER_COLOR[r.suitability_tier] || "#aaa" }}>
                    {r.suitability_tier || "—"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DhssUniversePicker;
